import { useState, useEffect } from 'react';
import { Formik, Form, yupToFormErrors } from 'formik';
import { isEqual } from 'lodash';

import Yup from '../../../utils/yup';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import useMenus from '../../../context/MenuContext';
import useUsers from '../../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI } from '../../Interface';
import AlertFormReports from './AlertFormReports';
import AlertFormFields from './AlertFormFields';
import { cleanValues, generateAutocompleteItems } from '../../../utils/helper';
import masterDataService from '../../../service/masterData.service';
import alertFormService from '../../../service/Alert/alertForm.service';
import { useLoader } from '../../../provider/LoaderProvider';
import AlertFormTabs from './AlertFormTabs';
import { AlertUnitEnum } from '../../../config/alert';
import AlertFormDialog from './AlertFormDialog';

const AlertFormPage = () => {
  const { showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    views: [],
  });
  const [autocompleteData, setAutoCompleteData] = useState({
    user: [],
    views: [],
  });
  const [submenu, setSubmenu] = useState(null);

  const [alertFormDialogOpen, setAlertFormDialogOpen] = useState(false);

  const defRecipient = {
    id: null,
    userId: null,
  };

  const defSchedule = {
    id: null,
    unit: AlertUnitEnum.MINUTE,
    every: 1,
    time: null,
    weekday: null,
    monthDay: null,
  };

  const def = {
    id: null,
    name: '',
    active: true,
    view: '',
    description: '',
    alertRecipient: [],
    alertSchedule: defSchedule,
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        alertFormService
          .getViews()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    showLoader();

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          views: resp[1],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          views: generateAutocompleteItems(aData.views, 'TABLE_NAME', 'TABLE_NAME'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);

    initDefaultValues();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('alertForm');
    setSubmenu(submenuId);
    setCanEdit(true); //getCanEdit(submenuId);
  }, [menus]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    view: Yup.string().required(),
    active: Yup.boolean(),
    description: Yup.string().nullable(),
    alertRecipient: Yup.array().of(
      Yup.object().shape({
        userId: Yup.number().required(),
      })
    ),
    alertSchedule: Yup.object().shape({
      unit: Yup.string().required(),
      every: Yup.number().required(),
      time: Yup.string()
        .nullable()
        .when('unit', (unit, schema) =>
          unit === AlertUnitEnum.DAY || unit === AlertUnitEnum.WEEK || unit === AlertUnitEnum.MONTH
            ? schema.required()
            : schema
        ),
      weekday: Yup.number()
        .nullable()
        .when('unit', (unit, schema) => (unit === AlertUnitEnum.WEEK ? schema.required() : schema)),
      monthDay: Yup.number()
        .nullable()
        .when('unit', (unit, schema) => (unit === AlertUnitEnum.MONTH ? schema.required() : schema)),
    }),
  });

  const valuesToForm = () => {
    const { id, name, active, view, description, alertRecipient, alertSchedule } = selectedValues;

    setInitValues({
      id,
      name,
      active,
      view,
      description,
      alertRecipient: alertRecipient.map((ar) => ({
        id: ar.id,
        userId: ar.userId,
      })),
      alertSchedule: {
        id: alertSchedule?.id ?? null,
        unit: alertSchedule?.unit ?? null,
        every: alertSchedule?.every ?? null,
        time: alertSchedule?.time ?? null,
        weekday: alertSchedule?.weekday ?? null,
        monthDay: alertSchedule?.monthDay ?? null,
      },
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
    };

    alertFormService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
    };

    alertFormService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    alertFormService.deleteData(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  const validate = async (values) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      return {};
    } catch (err) {
      const formErrors = yupToFormErrors(err);

      if (formErrors.alertRecipient) {
        addNotification({
          content: 'Kérjük, ellenőrizze a riasztás címzettjeit!',
          type: NotificationType.WARNING,
        });
      }

      if (formErrors.alertSchedule) {
        addNotification({
          content: 'Kérjük, ellenőrizze a riasztás ütemezését!',
          type: NotificationType.WARNING,
        });
      }

      addNotification({
        content: 'Kérjük, javítsa a hibásan kitöltött mezőket!',
        type: NotificationType.WARNING,
      });

      // Itt is eléred a hibákat: formErrors
      return formErrors;
    }
  };

  const openAlertFormDialog = () => {
    setAlertFormDialogOpen(true);
  };

  const handleAlertFormDialogClose = () => {
    setAlertFormDialogOpen(false);
  };

  return (
    <>
      <FormBase
        name="AlertForm"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validateOnChange={false}
                validateOnBlur={false}
                validate={validate}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm, values }) => (
                  <Form>
                    <AlertFormFields
                      viewField={viewField}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                      openAlertFormDialog={openAlertFormDialog}
                    />

                    <AlertFormTabs
                      submenuId={submenu}
                      selectedValues={selectedValues}
                      setDefaultValues={setDefaultValues}
                      viewField={viewField}
                      autocompleteOptions={autocompleteOptions}
                      autocompleteData={autocompleteData}
                      disabled={viewField}
                    />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                    <AlertFormDialog
                      open={alertFormDialogOpen}
                      onClose={handleAlertFormDialogClose}
                      autocompleteData={autocompleteData}
                      name={values.name}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <AlertFormReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autocompleteOptions={autocompleteOptions}
            autocompleteData={autocompleteData}
            submenuId={submenu}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

export default AlertFormPage;
